<script setup lang="ts">
import {
  ElForm,
  ElFormItem,
  ElCollapseItem,
  ElInput,
  ElTag,
  ElSwitch,
  ElTooltip,
  type FormInstance,
  type FormRules,
  type FormItemInstance,
} from "element-plus";
import { reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import azureActiveDirectoryLogo from "@/assets/images/azure-active-directory-icon.svg";
import CoreSaveConfirm from "@/components/common/CoreSaveConfirm/CoreSaveConfirm.vue";
import { CONFIRM } from "@/stores/constants";
import useSaveStatusStore from "@/stores/saveStatus";
import type { InputInstance } from "element-plus/es/components/input/src/input";
import { useValidation } from "@/composables/validation";

export type ExpandableBoxListItem = {
  id: string;
  numericId?: number;
  title: string;
  order?: number;
  tag?: string;
  active?: boolean;
  uneditable?: boolean;
  required?: boolean;
  approval?: string[];
  synced?: boolean;
  disableTitleEditing?: boolean;
};

export interface Props {
  id: string;
  title: string;
  tag?: string;
  active?: boolean;
  disabled?: boolean;
  disableToggle?: boolean;
  disableOrdering?: boolean;
  disabledExpand?: boolean;
  uneditable?: boolean;
  expanded: boolean;
  isDragging: boolean;
  orderable: boolean;
  isAzureAdSynced?: boolean;
  isCustomField?: boolean;
  disableTitleEditing: boolean;
  formRules?: FormRules;
  characterLimit?: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "editing-title", editing: boolean): void;
  (e: "change-title", title: string): void;
  (e: "change-active", active: boolean): void;
  (e: "has-expanded", value: boolean): void;
}>();

const { t, locale } = useI18n();
const { validateFormItem } = useValidation();
const saveStatusStore = useSaveStatusStore();

const updateTitleFormRef = ref<FormInstance>();
const titleInputRef = ref<InputInstance>();
const formRefId = ref<FormItemInstance>();
const titleRef = ref<FormItemInstance>();
const updateTitleForm = reactive({
  title: props.title,
  id: props.id,
});

defineExpose({
  updateTitleFormRef,
  titleInputRef,
});

watch(
  () => props.title,
  () => {
    if (!props.expanded) {
      updateTitleForm.title = props.title;
    }
  }
);

watch(
  () => props.expanded,
  (value) => {
    emit("has-expanded", value);
    if (value) {
      updateTitleFormRef.value?.clearValidate();
      saveStatusStore.setSaveStatus(
        `expandableBoxItem${props.id}`,
        CONFIRM.SAVED
      );
      return;
    }

    updateTitleFormRef.value?.resetFields();
    saveStatusStore.setSaveStatus(
      `expandableBoxItem${props.id}`,
      CONFIRM.SAVED
    );
    updateTitleForm.title = props.title;
  }
);

const submit = async () => {
  emit("editing-title", false);

  if (props.title === updateTitleForm.title) return;

  try {
    await updateTitleFormRef.value?.validate();
  } catch (error) {
    return;
  }
  emit("change-title", updateTitleForm.title.trim());
};

const onTitleKeyDown = async (e: KeyboardEvent | Event) => {
  if ("code" in e) {
    if (e.code.toLowerCase() === "space") {
      updateTitleForm.title += " ";
    }
  }
};

const handleInput = () => {
  updateTitleFormRef.value?.clearValidate();
  saveStatusStore.setSaveStatus(`expandableBoxItem${props.id}`, CONFIRM.SAVED);
};

watch(locale, () => {
  const fieldRefs = { titleRef: titleRef.value, formRefId: formRefId.value };
  validateFormItem(fieldRefs);
});
</script>

<template>
  <el-collapse-item
    :name="id"
    :class="[
      'expandable-row',
      { uneditable, inactive: active === false, disabled },
    ]"
    :disabled="
      uneditable ||
      disabled ||
      (isAzureAdSynced && isCustomField) ||
      disabledExpand
    "
  >
    <template #title>
      <div class="arrow-container">
        <font-awesome-icon
          :icon="['fal', expanded ? 'chevron-down' : 'chevron-right']"
          :class="[
            'arrow-icon',
            { disabled: disabled || disabledExpand, uneditable },
          ]"
        />
      </div>
      <el-form
        v-if="expanded && !disableTitleEditing"
        ref="updateTitleFormRef"
        class="title-input"
        :model="updateTitleForm"
        :rules="formRules"
        @keydown.enter.prevent="submit"
      >
        <el-form-item
          ref="titleRef"
          :class="{ 'is-error': formRefId?.validateState === 'error' }"
          prop="title"
        >
          <el-input
            ref="titleInputRef"
            v-model="updateTitleForm.title"
            :maxlength="characterLimit"
            @click.stop
            @keydown.space.stop.prevent="onTitleKeyDown"
            @blur="submit"
            @input="handleInput"
            @focus="emit('editing-title', true)"
          >
            <template #suffix>
              <CoreSaveConfirm
                :status="
                  saveStatusStore.getSaveStatuses[`expandableBoxItem${id}`]
                "
              />
            </template>
          </el-input>
        </el-form-item>
        <el-form-item
          ref="formRefId"
          class="id-form-item"
          prop="id"
        />
      </el-form>
      <span
        v-else
        class="uneditable-title"
      >
        {{ title }}
      </span>
      <div
        class="action-items"
        @click="(event) => event.stopPropagation()"
        @keyup.enter="(event) => event.stopPropagation()"
      >
        <slot
          v-if="!expanded"
          name="subAction"
        />
        <el-tag
          v-if="tag"
          round
          type="info"
          size="small"
          effect="dark"
        >
          {{ tag.toUpperCase() }}
        </el-tag>
        <el-switch
          v-if="active !== undefined"
          :model-value="active"
          size="small"
          :disabled="disabled || disableToggle"
          @click.stop
          @change="emit('change-active', Boolean($event))"
        />
        <el-tooltip
          v-if="props.isAzureAdSynced"
          :content="t('employeeSettings.profileFields.syncedTooltip')"
          placement="top"
          :show-after="500"
          :hide-after="0"
          :offset="12"
        >
          <img
            :src="azureActiveDirectoryLogo"
            :alt="t('employeeSettings.profileFields.adLogoAltText')"
            class="azure-logo"
          />
        </el-tooltip>
        <el-tooltip
          :disabled="isDragging || disabled || !orderable"
          :content="t('helperText.dragTooltip')"
          placement="top"
          :show-after="500"
          :hide-after="0"
          :offset="-8"
        >
          <div
            :class="[
              'handle-wrapper',
              {
                'handle-draggable':
                  !uneditable && !disabled && orderable && !disableOrdering,
              },
            ]"
          >
            <font-awesome-icon
              size="xl"
              :icon="['fas', 'grip-dots']"
              :class="[
                'handle-icon',
                {
                  disabled: disabled || disableOrdering,
                  hide: !orderable,
                },
              ]"
            />
          </div>
        </el-tooltip>
      </div>
    </template>
    <template #default="{ listItem }: { listItem: ExpandableBoxListItem }">
      <div class="collapse-content">
        <slot :list-item="listItem" />
      </div>
    </template>
  </el-collapse-item>
</template>

<style lang="css" scoped>
.expandable-row :deep(.so-collapse-item__header.is-active .title-input),
.expandable-row :deep(.so-collapse-item__header:not(.is-active)) {
  font-weight: var(--font-normal);
}

.expandable-row.inactive,
.expandable-row.disabled {
  --so-collapse-header-bg-color: var(--so-fill-color-lighter);
  --so-collapse-content-bg-color: var(--so-fill-color-lighter);
}

.expandable-row.uneditable:not(.disabled) :deep(.so-collapse-item__header) {
  color: var(--so-collapse-header-text-color);
  cursor: unset;
}

.expandable-row :deep(.so-collapse-item__header.focusing:focus:not(:hover)) {
  color: var(--so-collapse-header-text-color);
}

.arrow-container {
  flex-shrink: 0;
  width: 12px;
  margin: 0 17px;
}

.arrow-icon,
.handle-icon {
  color: var(--so-expandable-box-header-icon-color);
  cursor: pointer;
}

.arrow-icon.disabled,
.handle-icon.disabled {
  color: var(--so-text-color-disabled);
  cursor: not-allowed;
}

.arrow-icon.uneditable,
.handle-icon.hide {
  visibility: hidden;
}

.uneditable-title {
  margin-left: 15px;
  overflow: hidden;
  font-family: var(--font-family-base);
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.title-input {
  flex-grow: 1;
  width: auto;
  min-width: 0;
  height: var(--so-component-size-large);
}

.id-form-item {
  margin-top: -26px;
}

.action-items {
  display: flex;
  flex-shrink: 0;
  gap: 20px;
  align-items: center;
  padding-left: 20px;
  margin-left: auto;
}

.handle-wrapper {
  padding: 0 20px;
  margin-left: -20px;
}

.collapse-content {
  margin-left: 46px;
}

:deep(.so-collapse-item__arrow) {
  display: none;
}

:deep(.so-collapse-item__content) {
  padding-bottom: 15px;
}

:deep(.so-form-item__error) {
  white-space: pre;
}

.azure-logo {
  height: 18px;
  padding: 0 20px;
}
</style>
